import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  CheckSquare,
  Calendar,
  Target,
  AlertCircle,
  TrendingUp,
} from 'lucide-react'
import { TodayPriorities } from './TodayPriorities'
import { MyContents } from './MyContents'
import { TodoWidget } from './TodoWidget'
import { StatCard } from '@/components/common/StatCard'
import { Card, CardHeader } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { DashboardSkeleton } from './DashboardSkeleton'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import type { TaskPriority, TaskStatus } from '@/types'

interface MyTask {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  project?: { id: string; name: string } | string
  projectId?: string
  client?: { id: string; name: string } | string
  dueDate?: string
}

interface OperativeDashboardData {
  kpis?: {
    myOpenTasks?: number
    completedThisWeek?: number
    overdueItems?: number
    hoursLoggedToday?: number
  }
  myTasks?: {
    todo?: MyTask[]
    inProgress?: MyTask[]
    inReview?: MyTask[]
    done?: MyTask[]
  }
  upcomingDeadlines?: {
    id: string
    title?: string
    dueDate: string
    type?: string
  }[]
}

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  LOW: 'border-l-slate-300',
  MEDIUM: 'border-l-primary-400',
  HIGH: 'border-l-warning-500',
  URGENT: 'border-l-danger-500',
}

const PRIORITY_BADGE: Record<TaskPriority, 'neutral' | 'info' | 'warning' | 'danger'> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'danger',
}

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  LOW: 'Bassa',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
}

const EMPTY_DATA: OperativeDashboardData = { kpis: {} }

interface KanbanColumnProps {
  title: string
  tasks: MyTask[]
  statusColor: string
  emptyLabel: string
  onTaskClick?: (task: MyTask) => void
}

function KanbanColumn({ title, tasks, statusColor, emptyLabel, onTaskClick }: KanbanColumnProps) {
  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center gap-2 mb-3">
        <div className={cn('w-2 h-2 rounded-full flex-shrink-0', statusColor)} />
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          {title}
        </span>
        <Badge variant="neutral" size="sm" className="tabular-nums">{tasks.length}</Badge>
      </div>
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-xl">
            {emptyLabel}
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              onClick={() => onTaskClick?.(task)}
              className={cn(
                'bg-white rounded-xl border border-slate-200 shadow-card p-3',
                'border-l-2 hover:border-slate-300 transition-colors cursor-pointer',
                PRIORITY_COLORS[task.priority]
              )}
            >
              <p className="text-sm font-medium text-slate-900 leading-snug mb-1.5">
                {task.title}
              </p>
              {task.client && (
                <p className="text-xs text-slate-400 mb-2">
                  {typeof task.client === 'string' ? task.client : (task.client as any)?.name ?? ''}
                </p>
              )}
              {task.project && (
                <p className="text-xs text-primary-600 mb-2">
                  {typeof task.project === 'string' ? task.project : task.project?.name ?? ''}
                </p>
              )}
              <div className="flex items-center justify-between gap-2">
                <Badge variant={PRIORITY_BADGE[task.priority]} size="sm">
                  {PRIORITY_LABELS[task.priority]}
                </Badge>
                {task.dueDate && (
                  <div className="flex items-center gap-1 text-xs text-slate-400 tabular-nums">
                    <Calendar className="w-3 h-3" />
                    {new Date(task.dueDate).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export function OperativeDashboard() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)

  const { data, isLoading } = useQuery<OperativeDashboardData>({
    queryKey: ['dashboard', 'operative'],
    queryFn: async () => {
      const res = await apiClient.get<any>('/dashboard/operative')
      // Handle nested { type, data: {...} } structure from API
      const outer = res?.data ?? res
      const inner = outer?.data ?? outer
      return (inner ?? {}) as OperativeDashboardData
    },
    refetchInterval: 60_000,
    retry: false,
  })

  if (isLoading && !data) return <DashboardSkeleton />

  const d = data ?? EMPTY_DATA

  // Backend returns myTasks.items.{todo,inProgress,...} (arrays) and myTasks.{todo,...} (counts)
  // Mock/fallback uses myTasks.{todo,...} directly as arrays
  const rawTasks = d.myTasks as any
  const taskItems = {
    todo: (Array.isArray(rawTasks?.todo) ? rawTasks.todo : rawTasks?.items?.todo) ?? [],
    inProgress: (Array.isArray(rawTasks?.inProgress) ? rawTasks.inProgress : rawTasks?.items?.inProgress) ?? [],
    inReview: (Array.isArray(rawTasks?.inReview) ? rawTasks.inReview : rawTasks?.items?.inReview) ?? [],
    done: (Array.isArray(rawTasks?.done) ? rawTasks.done : rawTasks?.items?.done) ?? [],
  }

  const kpis = d.kpis as any

  function handleTaskClick(task: MyTask) {
    navigate(`/tasks?taskId=${task.id}`)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          La mia Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ciao {user?.firstName}, ecco i tuoi task per oggi
        </p>
      </div>

      {/* Today's Priorities + To-Do */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TodayPriorities />
        <TodoWidget />
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Task Aperti"
          value={kpis?.myOpenTasks ?? kpis?.openTasks ?? 0}
          icon={CheckSquare}
          tone="primary"
        />
        <StatCard
          label="Completati Settimana"
          value={kpis?.completedThisWeek ?? 0}
          icon={Target}
          tone="success"
          hint={
            <span className="inline-flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-success-500" />
              <span className="font-medium text-success-600 tabular-nums">+25%</span>
              <span className="text-slate-400">vs settimana scorsa</span>
            </span>
          }
        />
        <StatCard
          label="In Ritardo"
          value={kpis?.overdueItems ?? kpis?.upcomingDeadlines ?? 0}
          icon={AlertCircle}
          tone={(kpis?.overdueItems ?? kpis?.upcomingDeadlines ?? 0) > 0 ? 'danger' : 'neutral'}
        />
      </div>

      {/* My Contents - stato contenuti PED */}
      <MyContents />

      {/* Kanban board */}
      <Card padding="md">
        <CardHeader
          title="I Miei Task"
          description="Lavagna kanban personale"
        />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <KanbanColumn
            title="Da Fare"
            tasks={taskItems.todo}
            statusColor="bg-slate-400"
            emptyLabel="Nessun task"
            onTaskClick={handleTaskClick}
          />
          <KanbanColumn
            title="In Corso"
            tasks={taskItems.inProgress}
            statusColor="bg-primary-500"
            emptyLabel="Nessun task"
            onTaskClick={handleTaskClick}
          />
          <KanbanColumn
            title="In Review"
            tasks={taskItems.inReview}
            statusColor="bg-warning-500"
            emptyLabel="Nessun task"
            onTaskClick={handleTaskClick}
          />
          <KanbanColumn
            title="Completati"
            tasks={taskItems.done}
            statusColor="bg-success-500"
            emptyLabel="Nessun task"
            onTaskClick={handleTaskClick}
          />
        </div>
      </Card>

      {/* Upcoming deadlines */}
      <Card padding="md">
        <CardHeader
          title="Scadenze Imminenti"
          description="Prossimi 7 giorni"
          action={
            (d.kpis?.overdueItems ?? 0) > 0 ? (
              <Badge variant="danger" dot>
                {d.kpis?.overdueItems ?? 0} in ritardo
              </Badge>
            ) : null
          }
        />
        <div className="space-y-2">
          {(d.upcomingDeadlines ?? []).map((deadline) => {
            const dueDate = new Date(deadline.dueDate)
            const today = new Date()
            const isOverdue = dueDate < today
            const isToday = dueDate.toDateString() === today.toDateString()

            return (
              <div
                key={deadline.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-semibold tabular-nums',
                    isOverdue ? 'bg-danger-50 text-danger-600' :
                    isToday ? 'bg-warning-50 text-warning-600' :
                    'bg-slate-100 text-slate-600'
                  )}
                >
                  {dueDate.getDate()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{deadline.title ?? 'N/A'}</p>
                  <p className="text-xs text-slate-400">
                    {isOverdue ? 'In ritardo' : isToday ? 'Oggi' :
                    dueDate.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'short' })}
                  </p>
                </div>
                <Badge
                  variant={isOverdue ? 'danger' : isToday ? 'warning' : 'neutral'}
                  size="sm"
                >
                  {isOverdue ? 'Scaduto' : isToday ? 'Oggi' : 'In scadenza'}
                </Badge>
              </div>
            )
          })}
          {(d.upcomingDeadlines ?? []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">
              Nessuna scadenza nei prossimi 7 giorni
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}
